/**
 * Entity classification — given the structured pieces of a transaction's
 * narration (payment method + extracted name), guess whether the
 * counterparty is a merchant, a person, or unknown.
 *
 * Heuristic-only, no ML. Runs at parse time in the browser before encryption,
 * so the result becomes a stable hint stored alongside the transaction. The
 * user is always free to override per-row.
 */

import type { PaymentMethod } from "@paisa/types"

export type EntityHint = "merchant" | "person" | "unknown"

export interface ClassifyInput {
  paymentMethod: PaymentMethod | null
  extractedName: string | null
}

// Tokens that strongly indicate a corporate entity. Matched against the
// uppercased name with word boundaries so we don't false-positive on names
// that happen to contain these substrings.
const CORPORATE_TOKENS = [
  "PVT",
  "PRIVATE",
  "LIMITED",
  "LTD",
  "LLP",
  "INC",
  "CORP",
  "ENTERPRISES",
  "TECHNOLOGIES",
  "SOLUTIONS",
  "SERVICES",
] as const

const CORPORATE_REGEX = new RegExp(`\\b(${CORPORATE_TOKENS.join("|")})\\b`)

/**
 * Classify a parsed transaction's counterparty.
 *
 * Order of checks (first match wins):
 *  1. ATM withdrawal -> merchant (technically neither, but never a person).
 *  2. POS swipe -> merchant.
 *  3. Name contains a corporate suffix -> merchant.
 *  4. IMPS payment -> person (typical default in Indian banking).
 *  5. UPI payment -> person (default skews personal once corporates filter out).
 *  6. NEFT / RTGS -> merchant (typically employer / EMI / utility).
 *  7. ACH -> merchant (subscription / utility direct debit).
 *  8. Otherwise -> unknown (let the user decide).
 */
export function classifyEntity({ paymentMethod, extractedName }: ClassifyInput): EntityHint {
  if (paymentMethod === "atm") return "merchant"
  if (paymentMethod === "pos") return "merchant"

  const upper = (extractedName ?? "").toUpperCase()
  if (upper && CORPORATE_REGEX.test(upper)) return "merchant"

  if (paymentMethod === "imps") return "person"
  if (paymentMethod === "upi") return "person"
  if (paymentMethod === "neft" || paymentMethod === "rtgs") return "merchant"
  if (paymentMethod === "ach") return "merchant"

  return "unknown"
}
